// src/services/works/splitOptionGroups.js
//
// 노출속성(쿠팡이 옵션을 가르는 값)이 같은 옵션이 한 상품에 둘 이상이면 쿠팡이 받지 않는다.
// 예전에는 이때 AI 가 찾은 «차이점»으로 옵션 전체를 다시 묶어 25색 비니가 22개 상품으로 쪼개졌고,
// AI 가 목록에 안 넣은 옵션은 어느 상품에도 들어가지 않아 사라졌다(2026-10-16 PR261016-0975BF,
// 军绿色·卡其色 두 옵션이 둘 다 「카키(색상)」).
//
// 순서: ① 값을 서로 다르게 고친다(1688 원문 옵션명 사용) → ② 못 고치는 옵션만 다른 상품으로 뺀다.
// 어느 경우든 모든 옵션은 정확히 한 상품에 들어가야 한다(assertSplitCoverage).
// 순수 함수만 둔다(기댐 = 순수 파일 seoSpecGuard 색 표 하나) — 시험: __tests__/splitOptionGroups.test.mjs

// 1688 색상 원문 → 쿠팡 색상 이름(표는 seoSpecGuard CN_COLOR_KO). 번역이 둘을 같은 말로 옮겼을 때 가르는 데만 쓴다.
import { cnColorToKo } from './seoSpecGuard';
export { cnColorToKo };

const COLOR_ATTR_RE = /색상|컬러|색깔|color/i;

function parseJson(v) {
  if (v && typeof v === 'object') return v;
  if (v === undefined || v === null || v === '') return null; // 옵션 축이 없는 것(옵션 1축 상품) — 오류 아님
  try { return JSON.parse(v); } catch (e) { console.error('[splitOptionGroups] 옵션 원문 JSON 해석 실패:', e && e.message); return null; }
}

/** 옵션의 1688 원문 첫 옵션명과 영문 번역 */
export function optionSource(option) {
  const o1 = parseJson(option && option.option1) || {};
  return { cn: String(o1.name || ''), trans: String(o1.nameTrans || '') };
}

export function exposedOf(jsonDocument, index) {
  const ea = jsonDocument && jsonDocument[index] && jsonDocument[index].productPage &&
    jsonDocument[index].productPage.commonAttributes && jsonDocument[index].productPage.commonAttributes.exposedAttributes;
  return Array.isArray(ea) ? ea : null;
}

/** 쿠팡이 같은 옵션으로 보는 기준(노출속성 이름·값 전체) */
export function exposedKey(exposed) {
  if (!Array.isArray(exposed)) return null;
  return JSON.stringify(exposed.map(a => [a && a.attributeName, a && a.attributeValue]));
}

/** 같은 노출속성끼리 묶은 인덱스 그룹(2개 이상만) */
export function findDuplicateGroups(jsonDocument, count) {
  const byKey = new Map();
  for (let i = 0; i < count; i++) {
    const key = exposedKey(exposedOf(jsonDocument, i));
    if (key === null) continue;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(i);
  }
  return Array.from(byKey.values()).filter(g => g.length > 1);
}

function isFreeInput(attrName, schemaExposed) {
  const info = Array.isArray(schemaExposed) ? schemaExposed.find(s => s && s.name === attrName) : null;
  if (!info) return true;
  const dd = Array.isArray(info.dropdown) ? info.dropdown.filter(v => v !== null && v !== '') : [];
  return dd.length === 0;
}

// 「카키(색상)」 → { core: '카키', tail: '(색상)' }
function splitValue(v) {
  const m = String(v || '').match(/^(.*?)\s*(\([^()]*\))?$/);
  return { core: (m && m[1] || '').trim(), tail: (m && m[2]) || '' };
}

function titleCase(s) {
  return s.replace(/\b[a-z]/g, c => c.toUpperCase());
}

// 옵션명마다 다른 옵션명에 없는 낱말 — 「소형 7발 …」·「대형 7발 …」 → ['소형', '대형'] (모두 서로 달라야, 아니면 null)
function distinctNameWords(names) {
  const words = names.map(n => String(n || '').split(/[\s/,()·]+/).filter(Boolean));
  const diffs = words.map((w, i) => w.filter(t => words.every((o, j) => j === i || !o.includes(t))).join(' '));
  return diffs.every(Boolean) && new Set(diffs).size === diffs.length ? diffs : null;
}

/**
 * 노출속성이 겹치는 옵션의 값을 서로 다르게 고칠 방법을 찾는다(입력은 바꾸지 않는다).
 * @param {Array} options - product.options
 * @param {Array} jsonDocument - product.coupangDoc.jsonDocument (options 와 같은 순서)
 * @param {Array} [schemaExposed] - product.rocketRegInfo.schemaInfo.exposedAttributes (dropdown 이 있으면 못 고친다)
 * @returns {{ changes: Array<{index:number, optionNumber:string, attributeName:string, from:string, to:string, korName:string}>, unresolved: number[][] }}
 */
export function resolveDuplicateExposedValues(options, jsonDocument, schemaExposed) {
  const count = Array.isArray(options) ? options.length : 0;
  const changes = [];
  const unresolved = [];
  const current = [];
  for (let i = 0; i < count; i++) {
    const ea = exposedOf(jsonDocument, i);
    current.push(ea ? ea.map(a => ({ ...a })) : null);
  }
  const usedKeys = new Set(current.map(exposedKey).filter(k => k !== null));

  for (const group of findDuplicateGroups(jsonDocument, count)) {
    const attrs = current[group[0]];
    const editable = attrs.map((a, ai) => ({ a, ai })).filter(x => x.a && x.a.attributeValue && isFreeInput(x.a.attributeName, schemaExposed));
    // 색상만 고친다 — 사이즈·용량 같은 값을 바꾸면 상품 정보가 틀어진다(못 고치면 ② 분리로)
    const target = editable.find(x => COLOR_ATTR_RE.test(x.a.attributeName));
    if (!target) { unresolved.push(group.slice()); continue; }
    const { core, tail } = splitValue(target.a.attributeValue);

    // 원문 색상이 지금 값과 같은 옵션을 그대로 두고(번역이 맞는 쪽) 나머지를 고친다.
    const ranked = group.map(i => {
      const src = optionSource(options[i]);
      const ko = cnColorToKo(src.cn);
      return { i, src, ko, keep: !ko || ko.replace(/\s/g, '') === core.replace(/\s/g, '') };
    });
    ranked.sort((x, y) => (y.keep - x.keep) || (x.i - y.i));

    // 원문 색이 갈리지 않으면 옵션명끼리 서로 다른 낱말(소형/대형)로 전원 고친다 — 숫자(골드 2)는 구분을 지운다(515A72).
    // 원문에 색 근거가 없으면 색상 값 자체가 지어낸 것이라 차이 낱말만 쓴다.
    if (ranked.every(r => r.keep)) {
      const diffs = distinctNameWords(group.map(i => options[i].korName));
      const srcColor = ranked.some(r => r.ko || /色/.test(r.src.cn));
      const values = diffs && diffs.map(d => `${srcColor && core ? `${core} ${d}` : d}${tail}`);
      const nexts = values && group.map((i, gi) => current[i].map((a, ai) => (ai === target.ai ? { ...a, attributeValue: values[gi] } : a)));
      if (nexts && nexts.every(n => !usedKeys.has(exposedKey(n)))) {
        group.forEach((i, gi) => {
          usedKeys.add(exposedKey(nexts[gi]));
          changes.push({
            index: i,
            optionNumber: options[i].optionNumber,
            attributeName: target.a.attributeName,
            from: current[i][target.ai].attributeValue,
            to: values[gi],
            korName: String(options[i].korName || '')
          });
          current[i] = nexts[gi];
        });
        continue;
      }
    }

    const left = [];
    for (const r of ranked.slice(1)) {
      const candidates = [];
      if (r.ko && r.ko !== core) candidates.push(r.ko);
      if (/^[A-Za-z][A-Za-z\s-]*$/.test(r.src.trans.trim())) candidates.push(`${core} ${titleCase(r.src.trans.trim())}`);
      for (let n = 2; n <= count + 1; n++) candidates.push(`${core} ${n}`);
      let applied = false;
      for (const c of candidates) {
        const nextAttrs = current[r.i].map((a, ai) => (ai === target.ai ? { ...a, attributeValue: `${c}${tail}` } : a));
        const key = exposedKey(nextAttrs);
        if (usedKeys.has(key)) continue;
        usedKeys.add(key);
        const from = current[r.i][target.ai].attributeValue;
        current[r.i] = nextAttrs;
        const korName = String(options[r.i].korName || '');
        changes.push({
          index: r.i,
          optionNumber: options[r.i].optionNumber,
          attributeName: target.a.attributeName,
          from,
          to: `${c}${tail}`,
          korName: core && korName.includes(core) ? korName.replace(core, c) : korName
        });
        applied = true;
        break;
      }
      if (!applied) left.push(r.i);
    }
    if (left.length) unresolved.push([ranked[0].i, ...left]);
  }
  return { changes, unresolved };
}

/** 옵션의 1688 원문 옵션명 목록(option1·option2·option3 순) */
export function sourceNames(option) {
  return ['option1', 'option2', 'option3'].map(k => {
    const o = parseJson(option && option[k]);
    return o && o.name ? String(o.name) : '';
  });
}

/**
 * 노출속성을 1688 원문에 맞춘다 — 번역이 원문과 어긋난 두 가지를 바로잡는다(입력은 바꾸지 않는다).
 *  ① 원문 색이 다른데 번역 색상이 같다(军绿色·卡其色 → 둘 다 「카키」) → 다른 쪽 색상 이름을 원문대로 바꾼다.
 *  ② 원문이 같은 축(사이즈 등)인데 번역 값이 갈렸다(均码 → FREE / One Size) → 원문에 없는 차이라 많이 쓴 값 하나로 맞춘다.
 * 원문 색 축을 못 찾으면 아무것도 바꾸지 않는다(모르는 것을 추정하지 않는다).
 * @returns {{ changes: Array<{index:number, optionNumber:string, attributeName:string, from:string, to:string, korName:string}> }}
 */
export function alignExposedToSource(options, jsonDocument, schemaExposed) {
  const count = Array.isArray(options) ? options.length : 0;
  const changes = [];
  if (count < 2) return { changes };
  const cur = [];
  for (let i = 0; i < count; i++) {
    const ea = exposedOf(jsonDocument, i);
    if (!ea) return { changes };
    cur.push(ea.map(a => ({ ...a })));
  }
  const src = options.map(sourceNames);
  // 원문 색 축 = 이름이 색(사전·「色」)인 옵션이 절반 넘는 축
  let colorDim = -1;
  for (let k = 0; k < 3 && colorDim < 0; k++) {
    const hits = src.filter(n => n[k] && (cnColorToKo(n[k]) || /色/.test(n[k]))).length;
    if (hits * 2 > count) colorDim = k;
  }
  if (colorDim < 0) return { changes };
  const colorName = (cur[0].find(a => a && COLOR_ATTR_RE.test(a.attributeName)) || {}).attributeName;

  const record = (i, name, to) => {
    const a = cur[i].find(x => x.attributeName === name);
    if (!a || a.attributeValue === to) return;
    const prev = changes.find(c => c.index === i && c.attributeName === name);
    if (prev) prev.to = to;
    else changes.push({ index: i, optionNumber: options[i].optionNumber, attributeName: name, from: a.attributeValue, to, korName: String(options[i].korName || '') });
    a.attributeValue = to;
  };

  // ② 색 축 밖의 원문이 같은 옵션끼리는 색 아닌 노출속성 값이 같아야 한다
  const byRest = new Map();
  src.forEach((n, i) => {
    const key = JSON.stringify(n.filter((_, k) => k !== colorDim));
    if (!byRest.has(key)) byRest.set(key, []);
    byRest.get(key).push(i);
  });
  for (const idxs of byRest.values()) {
    if (idxs.length < 2) continue;
    const names = cur[idxs[0]].map(a => a.attributeName).filter(n => n !== colorName);
    for (const name of names) {
      const tally = new Map();
      idxs.forEach(i => {
        const v = (cur[i].find(a => a.attributeName === name) || {}).attributeValue;
        if (v !== undefined) tally.set(v, (tally.get(v) || 0) + 1);
      });
      if (tally.size < 2) continue;
      const best = Array.from(tally.entries()).sort((x, y) => y[1] - x[1])[0][0];
      idxs.forEach(i => record(i, name, best));
    }
  }

  // ① 원문 색이 다르면 번역 색상도 달라야 한다
  if (colorName && isFreeInput(colorName, schemaExposed)) {
    const colorOf = i => (cur[i].find(a => a.attributeName === colorName) || {}).attributeValue || '';
    const byValue = new Map();
    for (let i = 0; i < count; i++) {
      const v = colorOf(i);
      if (!v) continue;
      if (!byValue.has(v)) byValue.set(v, []);
      byValue.get(v).push(i);
    }
    const used = new Map(); // 색상값 → 그 값을 쓰는 원문 색 이름들
    const own = (v, cn) => { if (!used.has(v)) used.set(v, new Set()); used.get(v).add(cn); };
    for (let i = 0; i < count; i++) own(colorOf(i), src[i][colorDim]);
    for (const [value, idxs] of byValue.entries()) {
      const cns = Array.from(new Set(idxs.map(i => src[i][colorDim])));
      if (cns.length < 2) continue;
      const { core, tail } = splitValue(value);
      // 번역이 맞는 원문(사전 번역 = 지금 값, 「카키색」=「카키」)을 그대로 두고 나머지 원문 색을 고친다.
      // 맞는 원문이 없으면 사전에 없는 원문 하나만 두고, 그것도 없으면 전부 사전 이름으로 바꾼다.
      const norm = v => String(v || '').replace(/\s/g, '').replace(/색$/, '');
      const fits = cn => { const ko = cnColorToKo(cn); return !!ko && norm(ko) === norm(core); };
      const keepCn = cns.find(fits) || cns.find(cn => !cnColorToKo(cn)) || null;
      for (const cn of cns) {
        if (cn === keepCn) continue;
        const sample = idxs.find(i => src[i][colorDim] === cn);
        const trans = optionSource(options[sample]).trans.trim();
        const ko = cnColorToKo(cn);
        const cand = [];
        if (ko) cand.push(ko);
        if (/^[A-Za-z][A-Za-z\s-]*$/.test(trans)) cand.push(`${core} ${titleCase(trans)}`);
        for (let n = 2; n <= count + 1; n++) cand.push(`${core} ${n}`);
        const pick = cand.find(c => {
          const v = `${c}${tail}`;
          if (v === value) return false; // 지금 겹친 값
          const owners = used.get(v);
          return !owners || Array.from(owners).every(o => o === cn);
        });
        if (!pick) continue;
        const to = `${pick}${tail}`;
        used.get(value).delete(cn);
        own(to, cn);
        idxs.filter(i => src[i][colorDim] === cn).forEach(i => {
          record(i, colorName, to);
          const c = changes.find(x => x.index === i && x.attributeName === colorName);
          const base = c && [core, core.replace(/색$/, '')].find(x => x && c.korName.includes(x));
          if (base) c.korName = c.korName.replace(base, pick);
        });
      }
    }
  }
  return { changes };
}

/**
 * 노출속성이 겹치지 않게 최소한으로 나눈다 — 겹치는 옵션만 다음 상품으로 간다.
 * @returns {Array<{additionalAttributeKey:string, additionalAttributes:Array, options:Array}>} 첫 그룹 = 원본 상품
 */
export function buildMinimalSplitGroups(options, jsonDocument) {
  const groups = [];
  (options || []).forEach((option, i) => {
    const key = exposedKey(exposedOf(jsonDocument, i));
    let g = groups.find(x => key === null || !x.keys.has(key));
    if (!g) { g = { keys: new Set(), options: [] }; groups.push(g); }
    if (key !== null) g.keys.add(key);
    g.options.push(option);
  });
  return groups.map((g, gi) => {
    const label = gi === 0 ? '' : String(g.options[0].korName || '').split('/')[0].trim();
    return { additionalAttributeKey: label, additionalAttributes: label ? [{ value: label }] : [], options: g.options };
  });
}

/**
 * AI 가 목록에 넣지 않은 옵션을 노출속성이 겹치지 않는 첫 그룹(대개 원본 상품)에 넣는다.
 * groups 를 직접 고친다.
 */
export function placeUnlistedOptions(groups, options, jsonDocument) {
  const listed = new Set();
  groups.forEach(g => g.options.forEach(o => listed.add(o.optionNumber)));
  const keyOf = (o) => exposedKey(exposedOf(jsonDocument, options.indexOf(o)));
  const placed = [];
  options.forEach(o => {
    if (listed.has(o.optionNumber)) return;
    const key = keyOf(o);
    let g = groups.find(x => key === null || !x.options.some(y => keyOf(y) === key));
    if (!g) {
      const label = String(o.korName || '').split('/')[0].trim();
      g = { additionalAttributeKey: label, additionalAttributes: [{ value: label }], options: [] };
      groups.push(g);
    }
    g.options.push(o);
    listed.add(o.optionNumber);
    placed.push(o.optionNumber);
  });
  return placed;
}

/**
 * 모든 옵션이 정확히 한 그룹에 들어갔는지 — 아니면 분리하지 않는다.
 * @returns {{ ok: boolean, missing: string[], duplicated: string[], extra: string[] }}
 */
export function assertSplitCoverage(options, groups) {
  const want = (options || []).map(o => o.optionNumber);
  const seen = new Map();
  (groups || []).forEach(g => (g.options || []).forEach(o => seen.set(o.optionNumber, (seen.get(o.optionNumber) || 0) + 1)));
  const missing = want.filter(n => !seen.has(n));
  const duplicated = Array.from(seen.entries()).filter(([, c]) => c > 1).map(([n]) => n);
  const wantSet = new Set(want);
  const extra = Array.from(seen.keys()).filter(n => !wantSet.has(n));
  return { ok: !missing.length && !duplicated.length && !extra.length, missing, duplicated, extra };
}
